import { useCallback, useRef } from "react";
import {
  type MediaViewerGestureLock,
  isMediaViewerSwipeBlocked,
} from "@/lib/native/mediaViewerGestureLock";

type SwipeDismissOptions = {
  gestureLockRef?: React.RefObject<MediaViewerGestureLock>;
};

/** Swipe down on fullscreen overlays to dismiss (native-friendly). */
export function useFullscreenSwipeDismiss(
  onDismiss: () => void,
  options?: SwipeDismissOptions,
) {
  const start = useRef<{ x: number; y: number } | null>(null);
  const maxTouches = useRef(0);
  const gestureLockRef = options?.gestureLockRef;

  const resetTracking = useCallback(() => {
    start.current = null;
    maxTouches.current = 0;
  }, []);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      maxTouches.current = Math.max(maxTouches.current, e.touches.length);
      if (
        isMediaViewerSwipeBlocked(
          gestureLockRef?.current,
          maxTouches.current,
        )
      ) {
        start.current = null;
        return;
      }
      start.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    },
    [gestureLockRef],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      maxTouches.current = Math.max(maxTouches.current, e.touches.length);
      if (
        e.touches.length >= 2 ||
        isMediaViewerSwipeBlocked(
          gestureLockRef?.current,
          maxTouches.current,
        )
      ) {
        start.current = null;
      }
    },
    [gestureLockRef],
  );

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (
        isMediaViewerSwipeBlocked(
          gestureLockRef?.current,
          maxTouches.current,
        ) ||
        !start.current
      ) {
        resetTracking();
        return;
      }
      const touch = e.changedTouches[0];
      const dy = touch.clientY - start.current.y;
      const dx = Math.abs(touch.clientX - start.current.x);
      if (dy > 55 && dy > dx * 1.15) {
        onDismiss();
      }
      resetTracking();
    },
    [gestureLockRef, onDismiss, resetTracking],
  );

  return { onTouchStart, onTouchMove, onTouchEnd };
}
